<template>
    <DataTable
        v-model:expanded-rows="expandedRows"
        class="emd-data-table"
        data-key="id"
        :pt="{
            rowExpansionCell: 'border-transparent',
            emptyMessageCell: 'border-transparent this-has-no-effect',
            thead: 'font-inter',
        }"
        :row-class="getRowClasses"
        :table-class
        :value="reactivePageData.emds"
        @animationend="onAnimationEnd"
        @row-click="toggleRowExpand"
    >
        <template #empty>
            <p class="empty-emd-table p-4 text-center text-lg text-ds-primary-500">
                Early Deposit requests you create will appear here.
            </p>
        </template>
        <template #header>
            <p
                v-if="pageFetchError && !isAbortError(pageFetchError)"
                class="text-danger mb-4 rounded-md bg-ds-red-100 px-6 py-4 text-base"
            >
                There was an error fetching data for this table. Please try again, or contact support if you continue to
                experience the problem.
            </p>
            <DsTextInput
                v-model.trim="searchTerm"
                ref="searchInput"
                class="max-w-72"
                type="text"
            >
                <template #leading>
                    <DsIcon
                        icon="fa-magnifying-glass"
                        size="md"
                    />
                </template>
                <template
                    v-if="searchTerm"
                    #trailing
                >
                    <button
                        aria-label="Clear search"
                        type="button"
                        @click="clearSearch"
                    >
                        <DsIcon
                            class="cursor-pointer"
                            icon="fa-solid fa-circle-xmark"
                            size="md"
                        />
                    </button>
                </template>
            </DsTextInput>
        </template>
        <Column
            body-class="font-medium border-x-transparent cursor-pointer"
            class="address-col"
            field="address"
            header="Property Address"
            :header-class
        >
            <template #body="{ data: rowData }: { data: EarlyDeposit }">
                <div class="show-expand-toggle flex items-center">
                    {{ rowData.address }}
                </div>
            </template>
        </Column>
        <Column
            body-class="border-x-transparent cursor-pointer"
            field="deposit_amount"
            header="Requested Amount"
            :header-class
        >
            <template #body="{ data }">
                <div class="flex items-center">
                    {{ formatMoney(data.deposit_amount / 100) }}
                </div>
            </template>
        </Column>

        <Column
            body-class="border-x-transparent cursor-pointer"
            field="branch.title.name"
            header="Company"
            :header-class
        />
        <Column
            body-class="border-x-transparent cursor-pointer"
            field="branch.name"
            header="Branch"
            :header-class
        />
        <Column
            body-class="border-x-transparent cursor-pointer"
            class="created-col"
            field="created_at"
            header="Created"
            :header-class
        >
            <template #body="{ data: rowData }: { data: EarlyDeposit }">
                {{ formatDate(rowData.created_at, MONTH_DAY_YEAR_FORMAT, rowData.created_at) }}
            </template>
        </Column>
        <Column
            body-class="border-x-transparent"
            class="actions-col"
            header="Actions"
            :header-class
            ><template #body="{ data: rowData }: { data: EarlyDeposit }">
                <div class="dim-other-btns flex justify-end gap-1 px-2">
                    <button
                        class="options-icon px-1 text-content-primary shadow-none transition-opacity duration-150"
                        title="Update early deposit"
                        @click.stop="setUpdateRef(rowData)"
                    >
                        <font-awesome-icon icon="fa-edit fas" />
                    </button>
                    <button
                        class="options-icon px-1 text-semantic-destructive-primary-default shadow-none transition-opacity duration-150"
                        title="Delete early deposit"
                        @click.stop="setDeleteRef(rowData)"
                    >
                        <font-awesome-icon icon="fa-times fas" />
                    </button>
                </div>
            </template>
        </Column>
        <template #expansion="{ data: rowData, index }: { data: EarlyDeposit; index: number }">
            <EarlyDepositTableRowDetails
                :key="index"
                :row-data
            />
        </template>
    </DataTable>

    <EarlyDepositTablePaginator v-if="reactivePageData.emds.length > 0" />

    <UpdateEarlyDepositModal @emdUpdated="onEmdUpdated" />

    <DeleteEarlyDepositModal @emdDeleted="onEmdDeleted" />
</template>

<script setup lang="ts">
import type { EarlyDeposit } from '@/api/interfaces/early-deposit-types';
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsTextInput from '@/components/design-system/form/input/text/DsTextInput.vue';
import {
    useDeleteEarlyDeposit,
    useEarlyDepositsPaged,
    useExpandedRows,
    useUpdateEarlyDeposit,
} from '@/services/early-deposit.service';
import { isAbortError } from '@/shared/utils/errors';
import { formatDate, MONTH_DAY_YEAR_FORMAT } from '@/shared/utils/format-date';
import { formatMoney } from '@/shared/utils/format-number';
import Column from 'primevue/column';
import type { DataTableRowClickEvent } from 'primevue/datatable';
import DataTable from 'primevue/datatable';
import { reactive, useTemplateRef, watch } from 'vue';
import DeleteEarlyDepositModal from './DeleteEarlyDepositModal.vue';
import EarlyDepositTablePaginator from './EarlyDepositTablePaginator.vue';
import EarlyDepositTableRowDetails from './EarlyDepositTableRowDetails.vue';
import UpdateEarlyDepositModal from './UpdateEarlyDepositModal.vue';

interface EarlyDepositClickEvent extends DataTableRowClickEvent {
    data: Pick<EarlyDeposit, 'id'>;
}

const { setDeleteRef } = useDeleteEarlyDeposit();
const { setUpdateRef } = useUpdateEarlyDeposit();

const { reactivePageData, pageFetchError, searchTerm, updateLocalEmd, refetchCurrentPage } = useEarlyDepositsPaged();
const { expandedRows, refreshExpandedRows } = useExpandedRows();

const searchInputRef = useTemplateRef('searchInput');

const clearSearch = () => {
    searchTerm.value = '';
    searchInputRef.value?.inputElement?.focus();
};

const tableClass = 'view-emd-table';
const headerClass = 'px-0 py-2';
watch(
    () => reactivePageData.emds,
    (newPage, oldPage) => {
        if (newPage?.length && oldPage?.length) {
            requestAnimationFrame(() => {
                document.querySelector(`.${tableClass}`)?.scrollIntoView({ behavior: 'smooth' });
            });
        }
    },
);

const highlightModifiedRow: { id: EarlyDeposit['id']; operation: '' | 'delete' | 'update' } = reactive({ id: '', operation: '' });

const onEmdDeleted = (deletedEmdId: EarlyDeposit['id']) => {
    highlightModifiedRow.id = deletedEmdId;
    highlightModifiedRow.operation = 'delete';
    delete expandedRows.value[deletedEmdId];
};

const onEmdUpdated = ({ id, address, deposit_amount }: EarlyDeposit) => {
    highlightModifiedRow.id = id;
    highlightModifiedRow.operation = 'update';
    updateLocalEmd({
        id,
        address,
        deposit_amount,
    });
    expandedRows.value = { ...expandedRows.value, refreshTable: true };
    delete expandedRows.value.refreshTable;
};

const REFETCH_PAGE = 'refetch-page';
const onAnimationEnd = async ({ animationName, target }: AnimationEvent) => {
    const isRowModAnimation = ['emd-updated', 'emd-deleted'].some(name => animationName.startsWith(name));
    if (!isRowModAnimation) return;

    if ((target as HTMLElement).classList.contains(REFETCH_PAGE)) {
        await refetchCurrentPage();
        refreshExpandedRows();
    }
    highlightModifiedRow.id = '';
    highlightModifiedRow.operation = '';
};

const toggleRowExpand = (arg: Pick<EarlyDepositClickEvent, 'data'>) => {
    const rowId = arg.data.id;
    const expandedRowsCopy = { ...expandedRows.value };

    if (rowId in expandedRowsCopy) delete expandedRowsCopy[rowId];
    else expandedRowsCopy[rowId] = true;

    expandedRows.value = expandedRowsCopy;
};

const getRowClasses = (rowData: EarlyDeposit) => {
    const RECENTLY_DELETED_CSS = `${REFETCH_PAGE} play-emd-deleted`;
    const RECENTLY_UPDATED_CSS = 'play-emd-updated';
    const modifiedId = highlightModifiedRow.id;
    const operation = highlightModifiedRow.operation;
    return {
        [`transition-color duration-100 row-id-${rowData.id}`]: true,
        'is-expanded-row': rowData.id in expandedRows.value,
        'hover:bg-ds-blue-100/50': rowData.id !== modifiedId, // avoid having hover style clobber animation
        [RECENTLY_DELETED_CSS]: operation === 'delete' && rowData.id === modifiedId,
        [RECENTLY_UPDATED_CSS]: operation === 'update' && rowData.id === modifiedId,
    };
};
</script>

<style lang="scss" scoped>
.emd-data-table {
    @apply m-auto max-w-screen-2xl font-inter sm:pr-2 legacy-md:pr-6;

    & :deep([data-pc-section='header']) {
        @apply border-t-0 border-b-ds-gray-200 bg-transparent px-0 !important;
    }
}

:deep(.view-emd-table) {
    > thead tr th {
        font-size: 1rem !important; // needed to override datatable.design.ts
    }

    .actions-col {
        width: 6ch;
    }

    .play-emd-deleted,
    .play-emd-deleted + [data-pc-section='rowexpansion'] {
        animation: emd-deleted 1s forwards;
    }

    .play-emd-updated {
        animation: emd-updated 2s;
    }
}

:deep(.font-inter) {
    button,
    input,
    optgroup,
    select,
    textarea,
    span,
    label {
        font-family: inherit; // needed to override font from app.scss
    }
}

@keyframes emd-deleted {
    from {
        @apply border-ds-red-100 bg-ds-red-100;
    }

    to {
        @apply line-through;
        opacity: 0;
    }
}

@keyframes emd-updated {
    50% {
        @apply bg-ds-green-100;
    }

    100% {
        background-color: initial;
    }
}

.dim-other-btns:has(button:hover) {
    button:not(:hover) {
        opacity: 0.5;
    }
}

.show-expand-toggle::before {
    --content: '+';

    content: var(--content);
    cursor: pointer;
    font: 0.75em/1 monospace;
    display: flex;
    align-items: center;
    border-radius: 100%;
    color: white;
    margin-right: 1ch;
    padding: 0.25em;
    padding-bottom: 0.3em; // better visual centering
    height: 1.15em;
    width: 1.15em;
    transition: background-color 150ms linear;

    @apply bg-ds-green-500;
}

.is-expanded-row .show-expand-toggle::before {
    --content: '-';
    @apply bg-ds-red-500;
}

.empty-emd-table {
    word-spacing: 0.05ch;
}
</style>
